import { useMemo, useState } from 'react'
import type { FilterSection } from '../components/ui/FilterPanel'
import type { OfimaPedidosClientFilters } from '../types/ofimaPedidos'
import {
  OFIMA_TIPO_DOCUMENTO_OPTIONS,
  emptyOfimaPedidosClientFilters
} from '../utils/ofimaPedidos'

type Option = { label: string; value: string }

export function usePedidosOfimaClientFilters() {
  const [showFilters, setShowFilters] = useState(false)
  const [filterByTipo, setFilterByTipo] = useState(false)
  const [selectedTipos, setSelectedTipos] = useState<Option[]>([])
  const [filterByFecha, setFilterByFecha] = useState(false)
  const [fechaDesde, setFechaDesde] = useState('')
  const [fechaHasta, setFechaHasta] = useState('')
  const [filterByNit, setFilterByNit] = useState(false)
  const [nit, setNit] = useState('')
  const [soloMultiplesRemisiones, setSoloMultiplesRemisiones] = useState(false)
  const [soloMultiplesFacturas, setSoloMultiplesFacturas] = useState(false)
  const [soloMultiplesNotasCredito, setSoloMultiplesNotasCredito] = useState(false)

  const clientFilters = useMemo<OfimaPedidosClientFilters>(() => ({
    ...emptyOfimaPedidosClientFilters(),
    tiposDocumento: filterByTipo ? selectedTipos.map((option) => option.value) : [],
    nit: filterByNit ? nit : '',
    fechaDesde: filterByFecha ? fechaDesde : '',
    fechaHasta: filterByFecha ? fechaHasta : '',
    soloMultiplesRemisiones,
    soloMultiplesFacturas,
    soloMultiplesNotasCredito
  }), [
    fechaDesde,
    fechaHasta,
    filterByFecha,
    filterByNit,
    filterByTipo,
    nit,
    selectedTipos,
    soloMultiplesFacturas,
    soloMultiplesNotasCredito,
    soloMultiplesRemisiones
  ])

  const hasClientFilters = clientFilters.tiposDocumento.length > 0
    || clientFilters.nit.trim() !== ''
    || clientFilters.fechaDesde !== ''
    || clientFilters.fechaHasta !== ''
    || clientFilters.soloMultiplesRemisiones
    || clientFilters.soloMultiplesFacturas
    || clientFilters.soloMultiplesNotasCredito

  const activeFiltersCount = [
    clientFilters.tiposDocumento.length > 0,
    clientFilters.nit.trim() !== '',
    clientFilters.fechaDesde !== '' || clientFilters.fechaHasta !== '',
    clientFilters.soloMultiplesRemisiones,
    clientFilters.soloMultiplesFacturas,
    clientFilters.soloMultiplesNotasCredito
  ].filter(Boolean).length

  const clearFilters = () => {
    setFilterByTipo(false)
    setSelectedTipos([])
    setFilterByFecha(false)
    setFechaDesde('')
    setFechaHasta('')
    setFilterByNit(false)
    setNit('')
    setSoloMultiplesRemisiones(false)
    setSoloMultiplesFacturas(false)
    setSoloMultiplesNotasCredito(false)
  }

  const filterSections: FilterSection[] = [
    {
      id: 'quick',
      title: 'Filtros rápidos',
      filters: [
        {
          id: 'tipo',
          label: 'Filtrar por tipo de documento',
          type: 'switch',
          checked: filterByTipo,
          onToggle: (checked) => {
            setFilterByTipo(checked)
            if (!checked) setSelectedTipos([])
          },
          selectProps: filterByTipo
            ? {
                isMulti: true,
                options: OFIMA_TIPO_DOCUMENTO_OPTIONS,
                value: selectedTipos,
                onChange: (newValue) => setSelectedTipos((newValue as Option[]) || []),
                placeholder: 'Seleccionar tipos...'
              }
            : undefined
        },
        {
          id: 'fecha',
          label: 'Filtrar por fecha',
          type: 'switch',
          checked: filterByFecha,
          onToggle: (checked) => {
            setFilterByFecha(checked)
            if (!checked) {
              setFechaDesde('')
              setFechaHasta('')
            }
          },
          dateRangeProps: filterByFecha
            ? {
                start: fechaDesde,
                end: fechaHasta,
                onStartChange: setFechaDesde,
                onEndChange: setFechaHasta
              }
            : undefined
        },
        {
          id: 'nit',
          label: 'Filtrar por NIT',
          type: 'switch',
          checked: filterByNit,
          onToggle: (checked) => {
            setFilterByNit(checked)
            if (!checked) setNit('')
          },
          textProps: filterByNit
            ? {
                value: nit,
                onChange: setNit,
                placeholder: 'Contiene este NIT'
              }
            : undefined
        }
      ]
    },
    {
      id: 'advanced',
      title: 'Documentos múltiples',
      filters: [
        {
          id: 'multi-remisiones',
          label: 'Varias remisiones',
          type: 'switch',
          checked: soloMultiplesRemisiones,
          onToggle: setSoloMultiplesRemisiones
        },
        {
          id: 'multi-facturas',
          label: 'Varias facturas',
          type: 'switch',
          checked: soloMultiplesFacturas,
          onToggle: setSoloMultiplesFacturas
        },
        {
          id: 'multi-notas',
          label: 'Varias notas crédito',
          type: 'switch',
          checked: soloMultiplesNotasCredito,
          onToggle: setSoloMultiplesNotasCredito
        }
      ]
    }
  ]

  return {
    showFilters,
    setShowFilters,
    clientFilters,
    hasClientFilters,
    activeFiltersCount,
    filterSections,
    clearFilters
  }
}
